"use client"
import { useMemo, useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Spline from "@splinetool/react-spline"
import { motion } from "framer-motion"
import GradientText from "../components/GradientText/GradientText.jsx"
import "../components/GradientText/GradientText.css"
import BlurText from "../components/BlurText/BlurText.jsx"

const features = [
  {
    icon: (
      <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    title: "AI Study Assistant",
    description: "Instantly generate notes summaries, practice quizzes, and interactive flashcards.",
  },
  {
    icon: (
      <svg className="w-5 h-5 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
    title: "Smart Task Manager",
    description: "Keep track of deadlines, exam dates, and daily assignments with priority status.",
  },
  {
    icon: (
      <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    title: "Attendance Tracker",
    description: "Monitor lecture schedules, calculate minimal targets, and predict status.",
  },
  {
    icon: (
      <svg className="w-5 h-5 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
    title: "Lost & Found Portal",
    description: "Search and report lost items across campuses with secure peer match alerts.",
  },
]

export default function LandingPage() {
  const [darkMode, setDarkMode] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const navigate = useNavigate()

  const rainbowColors = useMemo(() => ["#FF9E4A", "#F83CBD", "#9E4AFF", "#1F7EFB", "#4AE6FF"], [])

  return (
    <div
      data-theme={darkMode ? "dark" : "light"}
      className={`relative min-h-screen lg:h-screen w-full flex flex-col justify-between overflow-x-hidden lg:overflow-hidden transition-colors duration-500 ${
        darkMode ? "bg-[#090D16] text-[#E2E8F0]" : "bg-[#F8FAFC] text-[#0F172A]"
      }`}
      style={{
        fontFamily: "'Figtree', system-ui, -apple-system, sans-serif"
      }}
    >
      {/* ===== Glowing Background Blobs ===== */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div
          className={`absolute top-[-10%] left-[-10%] w-[45%] aspect-square rounded-full mix-blend-multiply filter blur-[120px] opacity-40 animate-blob ${
            darkMode ? "bg-purple-900/20" : "bg-purple-200/40"
          }`}
        />
        <div
          className={`absolute top-[10%] right-[-10%] w-[40%] aspect-square rounded-full mix-blend-multiply filter blur-[120px] opacity-40 animate-blob animation-delay-2000 ${
            darkMode ? "bg-blue-900/20" : "bg-blue-200/40"
          }`}
        />
      </div>

      {/* ===== Header / Navbar ===== */}
      <header className={`w-full z-40 border-b transition-colors duration-300 ${
        darkMode ? "bg-[#090D16]/80 border-slate-800" : "bg-[#F8FAFC]/80 border-slate-200"
      }`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3.5 md:px-12">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
            <div className="flex items-center justify-center h-8.5 w-8.5 rounded-xl font-extrabold text-sm bg-gradient-to-tr from-[#FF8D3A] via-[#F254B6] to-[#2D7FF7] text-white shadow-md">
              A
            </div>
            <GradientText
              className="text-lg font-extrabold tracking-tight"
              colors={rainbowColors}
              animationSpeed={8}
            >
              AutomateU
            </GradientText>
          </div>

          <div className="flex items-center gap-6">
            <button
              aria-label="Toggle theme"
              onClick={() => setDarkMode((v) => !v)}
              className={`group inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all duration-300 border ${
                darkMode
                  ? "bg-slate-900 border-slate-800 text-slate-350 hover:bg-slate-800"
                  : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm"
              }`}
            >
              <span className="inline-block h-3 w-3 rounded-full bg-gradient-to-tr from-[#FF9E4A] via-[#F83CBD] to-[#1F7EFB]" />
              {darkMode ? "Light" : "Dark"}
            </button>
            
            <button
              onClick={() => navigate("/login")}
              className={`inline-flex items-center justify-center rounded-xl px-4 py-1.5 text-xs font-bold transition-all duration-300 ${
                darkMode
                  ? "bg-white text-[#090D16] hover:bg-slate-100"
                  : "bg-[#0F172A] text-white hover:bg-slate-800"
              }`}
            >
              Sign In
            </button>
          </div>
        </div>
      </header>

      {/* ===== Main Hero Area ===== */}
      <main className="w-full max-w-7xl px-6 py-8 lg:py-0 flex-grow grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center z-10 self-center lg:max-h-[calc(100vh-140px)]">
        
        {/* Left Side: Copy, CTA, Larger Feature Grid */}
        <section className="lg:col-span-6 flex flex-col items-center lg:items-start text-center lg:text-left justify-center py-4">
          
          {/* Welcome Message */}
          <p className={`text-[10px] font-extrabold uppercase tracking-widest mb-2.5 ${
            darkMode ? "text-purple-400" : "text-purple-600"
          }`}>
            welcome to automateu!
          </p>

          {/* Headline */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5.5xl font-extrabold tracking-tight leading-[1.1] mb-5">
            Streamline your studies, <br/>
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#FF8D3A] via-[#F254B6] to-[#2D7FF7]">
              automate your success.
            </span>
          </h1>

          {/* Subheading */}
          <p className={`text-sm sm:text-base leading-relaxed mb-6 max-w-lg ${
            darkMode ? "text-slate-400" : "text-slate-600"
          }`}>
            AutomateU connects AI study tools, attendance tracking, task management, and campus networking into one beautiful space. Stop juggling tabs; start automating.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3.5 w-full max-w-md justify-center lg:justify-start mb-8">
            <button
              onClick={() => navigate("/login")}
              className="inline-flex items-center justify-center rounded-xl px-7 py-3.5 font-bold text-white bg-gradient-to-r from-[#FF8D3A] via-[#F254B6] to-[#2D7FF7] transition-all duration-350 hover:scale-[1.02] shadow-lg shadow-purple-500/10"
            >
              Get Started
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button
              onClick={() => navigate("/signup")}
              className={`inline-flex items-center justify-center rounded-xl px-6 py-3.5 font-semibold border transition-all duration-300 hover:scale-[1.02] ${
                darkMode
                  ? "border-slate-800 bg-slate-900/50 text-white hover:bg-slate-800"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-sm"
              }`}
            >
              Create Account
            </button>
          </div>

          {/* Expanded & Bigger Feature List */}
          <div className="w-full">
            <div className={`h-px w-full mb-4 ${darkMode ? "bg-slate-800" : "bg-slate-200"}`} />
            <h3 className={`text-xs font-black uppercase tracking-widest mb-3 ${darkMode ? "text-purple-400" : "text-purple-600"}`}>
              Explore Features
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {features.map((item, idx) => (
                <div
                  key={idx}
                  className={`flex items-start gap-3.5 p-3 rounded-xl border transition-all duration-200 hover:-translate-y-0.5 ${
                    darkMode
                      ? "bg-slate-900/40 border-slate-800/80 hover:bg-slate-900/60"
                      : "bg-white border-slate-100 hover:bg-slate-50 shadow-sm"
                  }`}
                >
                  <div className={`flex items-center justify-center p-2 rounded-lg shrink-0 ${
                    darkMode ? "bg-slate-850 text-white" : "bg-slate-100 text-slate-700"
                  }`}>
                    {item.icon}
                  </div>
                  <div className="text-left">
                    <h4 className="text-sm font-extrabold leading-tight mb-0.5">{item.title}</h4>
                    <p className={`text-[11px] leading-snug ${darkMode ? "text-slate-450" : "text-slate-500"}`}>{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Right Side: Free-floating 3D Spline Orb with Glowing hover and Tilting cards */}
        <section className="lg:col-span-5 flex flex-col items-center justify-center w-full py-4 relative lg:translate-x-14">
          
          {/* Orb Area containing Spline and floating elements */}
          <div
            className="w-full max-w-[400px] h-[280px] sm:h-[360px] scale-[0.8] sm:scale-100 origin-center relative flex items-center justify-center transition-all duration-500"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            
            {/* Glow Background on hover */}
            <div
              aria-hidden
              className={`absolute w-[350px] aspect-square rounded-full blur-[80px] transition-opacity duration-700 pointer-events-none z-0 ${
                isHovered ? "opacity-100 animate-glow-smooth" : "opacity-40"
              }`}
              style={{
                background: darkMode
                  ? "radial-gradient(circle, rgba(255,255,255,0.45) 0%, rgba(126,215,255,0.2) 50%, transparent 80%)"
                  : "radial-gradient(circle, rgba(255,158,74,0.7) 0%, rgba(248,60,189,0.5) 35%, rgba(31,126,251,0.3) 65%, transparent 90%)",
              }}
            />

            {/* Spline Object */}
            <div className="w-[280px] h-[280px] absolute z-10 flex items-center justify-center overflow-hidden pointer-events-auto">
              <Spline scene="https://prod.spline.design/sFVNZmY5tr8VKMry/scene.splinecode" />
            </div>

            {/* Card 1: Task Card (Top Left - Tilted Left) */}
            <div className={`absolute top-2 left-[-20px] z-20 flex flex-col gap-1.5 p-3 rounded-2xl border backdrop-blur-md w-38 rotate-[-6deg] hover:rotate-0 hover:scale-105 transition-all duration-300 shadow-lg animate-float-1 ${
              darkMode ? "bg-slate-950/80 border-slate-800/90" : "bg-white/90 border-slate-200"
            }`}>
              <div className="flex items-center gap-1.5">
                <div className="p-0.5 rounded text-amber-500 bg-amber-500/10 border border-amber-500/20">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <span className={`text-[9px] font-extrabold uppercase tracking-wider ${darkMode ? "text-slate-400" : "text-slate-550"}`}>Task</span>
              </div>
              <span className="text-[11px] font-bold truncate">Web Dev Quiz</span>
              <span className="text-[9px] text-amber-500 font-semibold leading-none">Due in 2 hours</span>
            </div>

            {/* Card 2: Quiz Card (Top Right - Tilted Right) */}
            <div className={`absolute top-12 right-[-20px] z-20 flex flex-col gap-1.5 p-3 rounded-2xl border backdrop-blur-md w-40 rotate-[8deg] hover:rotate-0 hover:scale-105 transition-all duration-300 shadow-lg animate-float-2 ${
              darkMode ? "bg-slate-950/80 border-slate-800/90" : "bg-white/90 border-slate-200"
            }`}>
              <div className="flex items-center gap-1.5">
                <div className="p-0.5 rounded text-emerald-500 bg-emerald-500/10 border border-emerald-500/20">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className={`text-[9px] font-extrabold uppercase tracking-wider ${darkMode ? "text-slate-400" : "text-slate-550"}`}>Quiz</span>
              </div>
              <span className="text-[11px] font-bold truncate">OS Practice Test</span>
              <span className="text-[9px] text-emerald-500 font-semibold leading-none">Score: 9/10</span>
            </div>

            {/* Card 3: Notes Card (Bottom Right - Tilted Left) */}
            <div className={`absolute bottom-4 right-2 z-20 flex flex-col gap-1.5 p-3 rounded-2xl border backdrop-blur-md w-38 rotate-[-4deg] hover:rotate-0 hover:scale-105 transition-all duration-300 shadow-lg animate-float-3 ${
              darkMode ? "bg-slate-950/80 border-slate-800/90" : "bg-white/90 border-slate-200"
            }`}>
              <div className="flex items-center gap-1.5">
                <div className="p-0.5 rounded text-indigo-500 bg-indigo-500/10 border border-indigo-500/20">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12h6M9 16h6M7 8h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V10a2 2 0 012-2z" />
                  </svg>
                </div>
                <span className={`text-[9px] font-extrabold uppercase tracking-wider ${darkMode ? "text-slate-400" : "text-slate-550"}`}>AI Notes</span>
              </div>
              <span className="text-[11px] font-bold truncate">Physics Summary</span>
              <span className="text-[9px] text-indigo-500 font-semibold leading-none">Generated</span>
            </div>

            {/* Card 4: Attendance Status (Bottom Left) */}
            <div className={`absolute bottom-12 left-[-10px] z-20 flex flex-col gap-1 p-2.5 rounded-xl border backdrop-blur-md w-32 rotate-[4deg] hover:rotate-0 hover:scale-105 transition-all duration-300 shadow-lg ${
              darkMode ? "bg-slate-950/80 border-slate-800/90" : "bg-white/90 border-slate-200"
            }`}>
              <div className="flex justify-between items-center">
                <span className={`text-[9px] font-bold ${darkMode ? "text-slate-450" : "text-slate-500"}`}>Attendance</span>
                <span className="text-[10px] font-black text-emerald-500">86.4%</span>
              </div>
              <div className={`w-full h-1.5 rounded-full overflow-hidden ${darkMode ? "bg-slate-800" : "bg-slate-100"}`}>
                <div className="bg-gradient-to-r from-emerald-400 to-teal-500 h-full rounded-full w-[86.4%]" />
              </div>
            </div>

          </div>

          {/* Title Below Free-Floating Orb */}
          <div className="mt-4 flex flex-col items-center select-none z-20">
            <GradientText
              className="text-2xl font-black tracking-tight"
              colors={rainbowColors}
              animationSpeed={8}
            >
              AutomateU
            </GradientText>
            <span className={`text-xs font-bold uppercase tracking-widest mt-1 ${darkMode ? "text-slate-500" : "text-slate-400"}`}>
              all in one.
            </span>
          </div>
        </section>

      </main>

      {/* ===== Footer ===== */}
      <footer className={`w-full py-4 text-center text-[10px] border-t z-10 transition-colors duration-300 ${
        darkMode ? "border-slate-900 bg-slate-950/40 text-slate-500" : "border-slate-200 bg-slate-50/50 text-slate-450"
      }`}>
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row justify-between gap-2.5 items-center">
          <span>© 2026 AutomateU. All rights reserved.</span>
          <div className="flex gap-4 font-semibold">
            <a href="#" className={`transition-colors ${darkMode ? "hover:text-slate-350" : "hover:text-slate-650"}`}>Privacy</a>
            <a href="#" className={`transition-colors ${darkMode ? "hover:text-slate-350" : "hover:text-slate-650"}`}>Terms</a>
            <a href="#" className={`transition-colors ${darkMode ? "hover:text-slate-350" : "hover:text-slate-650"}`}>Support</a>
          </div>
        </div>
      </footer>

      {/* ===== Global Animations & Custom Styles ===== */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Figtree:wght@350;400;550;600;700;800;900&display=swap');

        /* Floating Animations for individual cards */
        @keyframes float1 {
          0%, 100% { transform: translateY(0px) rotate(-6deg); }
          50% { transform: translateY(-8px) rotate(-5deg); }
        }
        @keyframes float2 {
          0%, 100% { transform: translateY(0px) rotate(8deg); }
          50% { transform: translateY(-12px) rotate(9deg); }
        }
        @keyframes float3 {
          0%, 100% { transform: translateY(0px) rotate(-4deg); }
          50% { transform: translateY(-6px) rotate(-3deg); }
        }

        @keyframes glow-pulse-smooth {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.18); opacity: 1; }
        }

        .animate-glow-smooth {
          animation: glow-pulse-smooth 5s ease-in-out infinite;
        }

        .animate-float-1 {
          animation: float1 4.5s ease-in-out infinite;
        }
        .animate-float-2 {
          animation: float2 5.5s ease-in-out infinite;
        }
        .animate-float-3 {
          animation: float3 5s ease-in-out infinite;
        }

        /* Floating Animation for background blobs */
        @keyframes blob {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          50% { transform: translate(25px, -35px) scale(1.05); }
        }
        .animate-blob {
          animation: blob 12s ease-in-out infinite;
        }
        .animation-delay-2000 {
          animation-delay: 3s;
        }

        @keyframes gradient-slow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient-slow { animation: gradient-slow 22s ease-in-out infinite; }
      `}</style>
    </div>
  )
}
